<template>
  <div>
    <div class="d-flex justify-content-between my-2">
        <h4>Visualização</h4>
        <div class="banners-btns-wrapper">
          <div class="banner-btn-group">
            <span class="banner-btn-title">Banner Desktop</span>
            <div class="banner-btns">
              <input
                ref="bannerImageView"
                type="file"
                hidden
                @change="handleImageUpload($event)"
              >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                class="btn-icon"
                @click="$refs.bannerImageView.click()"
              >
                <feather-icon icon="PlusIcon" />
                Adicionar
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="danger"
                class="btn-icon"
                @click="removeImage($event)"
              >
                <feather-icon icon="Trash2Icon" />
                Remover
              </b-button>
            </div>
          </div>
          <div class="banner-btn-group">
            <span class="banner-btn-title">Banner Mobile</span>
            <div class="banner-btns">
              <input
                ref="bannerMobileImageView"
                type="file"
                hidden
                @change="handleMobileImageUpload($event)"
              >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="success"
                class="btn-icon"
                @click="$refs.bannerMobileImageView.click()"
              >
                <feather-icon icon="PlusIcon" />
                Adicionar
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="danger"
                class="btn-icon"
                @click="removeMobileImage($event)"
              >
                <feather-icon icon="Trash2Icon" />
                Remover
              </b-button>
            </div>
          </div>
        </div>
    </div>

    <div>
      <b-alert
          variant="primary"
          show
      >
        <div class="alert-body">
          <span>
            Para adicionar uma imagem, clique no botão <strong>Adicionar Imagem</strong> e selecione uma imagem.
            <ul>
              <li>Para melhor visualização, utilize imagens com dimensões de <strong>1600x340 pixels.</strong>.</li>
            </ul>
            </span>
        </div>
      </b-alert>
      <div class="painel-banner">
        <img
          id="imagem-banner"
          :src="itemData.imagem"
          :alt="itemData.nome"
        />
      </div>
      <div class="painel-banner painel-banner-mobile mt-2">
        <img
          id="imagem-banner-mobile"
          :src="itemData.imagem_mobile"
          :alt="itemData.nome + ' mobile'"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {avatarText} from '@core/utils/filter'
import {ref} from '@vue/composition-api'
import useProdutosList from '@/views/apps/produtos/list/useProdutosList'
import {BButton, BAlert} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BButton,
    BAlert,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'itemData',
    event: 'handleSubmit',
  },
  props: {
    itemData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const {resolveUserRoleVariant} = useProdutosList()
    const bannerImageView = ref(null)
      const bannerMobileImageView = ref(null)

    const maxSizeEnv = Number(process.env.VUE_APP_IMAGE_MAX_SIZE_MB)
    const maxImageSizeMb = Number.isFinite(maxSizeEnv) && maxSizeEnv > 0 ? maxSizeEnv : 5
    const maxImageSize = maxImageSizeMb * 1024 * 1024
    const maxImageDimension = 1920
    const compressionQuality = 0.92
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']

    const showWarning = message => {
      window.alert(message)
    }

    const validateImageType = file => allowedImageTypes.includes(file.type)

    const loadImage = file => new Promise((resolve, reject) => {
      const image = new Image()
      const objectUrl = URL.createObjectURL(file)

      image.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(image)
      }

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('Não foi possível ler a imagem selecionada.'))
      }

      image.src = objectUrl
    })

    const canvasToFile = (canvas, file, mimeType, quality) => new Promise(resolve => {
      canvas.toBlob(blob => {
        if (!blob) {
          resolve(file)
          return
        }

        resolve(new File([blob], file.name, {
          type: mimeType,
          lastModified: file.lastModified,
        }))
      }, mimeType, quality)
    })

    const hasTransparency = (context, width, height) => {
      const { data } = context.getImageData(0, 0, width, height)

      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
          return true
        }
      }

      return false
    }

    const compressImage = async file => {
      if (file.type === 'image/gif') {
        return file
      }

      const image = await loadImage(file)
      const needsResize = image.width > maxImageDimension || image.height > maxImageDimension
      const needsCompress = file.size > maxImageSize

      if (!needsResize && !needsCompress) {
        return file
      }

      const scale = Math.min(
        maxImageDimension / image.width,
        maxImageDimension / image.height,
        1,
      )
      const width = Math.max(1, Math.round(image.width * scale))
      const height = Math.max(1, Math.round(image.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const context = canvas.getContext('2d')
      context.drawImage(image, 0, 0, width, height)

      const isPng = file.type === 'image/png'
      const mimeType = isPng && hasTransparency(context, width, height)
        ? 'image/png'
        : 'image/jpeg'
      const quality = mimeType === 'image/jpeg' ? compressionQuality : undefined

      const compressedFile = await canvasToFile(canvas, file, mimeType, quality)

      if (compressedFile.size >= file.size) {
        return file
      }

      return compressedFile
    }

    const prepareImage = async file => {
      if (!validateImageType(file)) {
        showWarning('Selecione apenas imagens JPEG, PNG ou GIF.')
        return null
      }

      const compressedFile = await compressImage(file)

      if (compressedFile.size > maxImageSize) {
        showWarning(`A imagem ainda ficou acima de ${maxImageSizeMb}MB após a compressão automática.`)
        return null
      }

      return compressedFile
    }

    const handleImageUpload = async e => {
      const file = e.target.files[0]
      if (!file) return

      const preparedFile = await prepareImage(file)
      if (!preparedFile) return

      const reader = new FileReader()
      reader.readAsDataURL(preparedFile)
      reader.onload = () => {
        props.itemData.imagem = reader.result
      }
    }
    const removeImage = () => {
      document.getElementById('imagem-banner').src = ''
      emit('removeImagem')
    }

      // Banner Mobile
      const handleMobileImageUpload = async e => {
        const file = e.target.files[0]
        if (!file) return

        const preparedFile = await prepareImage(file)
        if (!preparedFile) return

        const reader = new FileReader()
        reader.readAsDataURL(preparedFile)
        reader.onload = () => {
          props.itemData.imagem_mobile = reader.result
        }
      }
      const removeMobileImage = () => {
        document.getElementById('imagem-banner-mobile').src = ''
        emit('removeImagemMobile')
      }

    return {
      resolveUserRoleVariant,
      avatarText,
      handleImageUpload,
      removeImage,
        handleMobileImageUpload,
        removeMobileImage,
      bannerImageView,
        bannerMobileImageView,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.painel-banner {
  position: relative;
  background-color: white;
  height: 320px;
}

.painel-banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

 .painel-banner-mobile {
  width: 400px;
  height: 85px;
  border: 2px dashed #e0e0e0;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border-radius: 16px;
}

.painel-banner-mobile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.banners-btns-wrapper {
  display: flex;
  gap: 32px;
  margin-bottom: 16px;
}
.banner-btn-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.banner-btn-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}
.banner-btns {
  display: flex;
  gap: 10px;
}

.banne-content {
  position: relative;
  height: 100%;
  justify-content: flex-end;
  padding: 0 0 10% 10%;
  z-index: 1;
}

.banne-content h4 {
  font-size: 20px;
  font-weight: 400;
  margin-bottom: 10px;
}

</style>
